import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import {
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  EllipsisHorizontalIcon,
  DocumentIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/solid';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const STATUS_CONFIG = {
  Returned: { amount: 1550, pending: 1, color: '#F59E0B', icon: ArrowPathIcon, bg: 'bg-amber-500' },
  Submitted: { amount: 163570, pending: 5, color: '#6366F1', icon: DocumentTextIcon, bg: 'bg-indigo-500' },
  Blocked: { amount: 0, pending: 0, color: '#EF4444', icon: XCircleIcon, bg: 'bg-rose-500' },
  'In Progress': { amount: 8400, pending: 2, color: '#3B82F6', icon: ClockIcon, bg: 'bg-blue-500' },
  Approved: { amount: 120500, pending: 0, color: '#10B981', icon: CheckCircleIcon, bg: 'bg-emerald-500' },
  Committed: { amount: 45000, pending: 0, color: '#8B5CF6', icon: ArrowRightIcon, bg: 'bg-violet-500' },
};

const EXPENSE_CATEGORIES = [
  "Airfare", "Lodging", "Meals & Entertainment", "Ground Transportation", 
  "Office Supplies", "Software/SaaS", "Telecom/Mobile", "Health/Wellness", "Other"
];

export default function ProFintechDashboard() {
  const [showForm, setShowForm] = useState(false);
  const [activeRange, setActiveRange] = useState('60D');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("Airfare");
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [myRequests, setMyRequests] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const fileInputRef = useRef(null);

  // --- DYNAMIC DATA MAPPING ---
  const [chartValues, setChartValues] = useState([0, 0, 0, 0, 0, 0]);
  const [realExpenseData, setRealExpenseData] = useState({
    Submitted: { amount: 0, pending: 0 },
    'In Progress': { amount: 0, pending: 0 },
    Approved: { amount: 0, pending: 0 },
    Rejected: { amount: 0, pending: 0 }
  });

  useEffect(() => {
    const dataMap = {
      '30D': [5, 20, 2, 10, 40, 20],
      '60D': [12, 45, 5, 25, 80, 40],
      '90D': [25, 90, 15, 60, 150, 85]
    };
    setChartValues(dataMap[activeRange]);
  }, [activeRange]);

  const handleSubmitExpense = async () => {
    if (!amount || !description) {
      alert('Please fill in all required fields');
      return;
    }

    const formData = new FormData();
    formData.append('emp_code', localStorage.getItem('emp_code'));
    formData.append('category', selectedCategory);
    formData.append('amount', amount);
    formData.append('description', description);
    if (selectedCategory === 'Other' && customCategory) {
      formData.append('custom_category', customCategory);
    }
    if (selectedFile) {
      formData.append('receipt', selectedFile);
    }

    try {
      const response = await fetch('/unified_approval/expense/submit', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        alert('Expense request submitted successfully!');
        setShowForm(false);
        // Reset form
        setAmount('');
        setDescription('');
        setCustomCategory('');
        setSelectedFile(null);
        setSelectedCategory('Airfare');
        // Refresh requests
        fetchMyRequests();
      } else {
        alert('Failed to submit expense request');
      }
    } catch (error) {
      console.error('Error submitting expense:', error);
      alert('Error submitting expense request');
    }
  };

  const fetchMyRequests = async () => {
    try {
      const empCode = localStorage.getItem('emp_code');
      const response = await fetch(`/unified_approval/expense/my?emp_code=${empCode}`);
      if (response.ok) {
        const data = await response.json();
        setMyRequests(data);
        
        // Calculate real expense data
        const statusCounts = {
          'PENDING_MANAGER': { amount: 0, pending: 0 },
          'PENDING_HR': { amount: 0, pending: 0 },
          'HR_APPROVED': { amount: 0, pending: 0 },
          'MANAGER_REJECTED': { amount: 0, pending: 0 }
        };
        
        data.forEach(req => {
          try {
            const expenseData = JSON.parse(req.employee_reason);
            const amount = expenseData.amount || 0;
            const status = req.status;
            
            if (statusCounts[status]) {
              statusCounts[status].amount += amount;
              statusCounts[status].pending += 1;
            }
          } catch (e) {
            console.log('Error parsing expense data:', e);
          }
        });
        
        setRealExpenseData({
          'Submitted': statusCounts['PENDING_MANAGER'],
          'In Progress': statusCounts['PENDING_HR'],
          'Approved': statusCounts['HR_APPROVED'],
          'Rejected': statusCounts['MANAGER_REJECTED']
        });
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const handleDownloadLedger = () => {
    try {
      const emp_code = localStorage.getItem('emp_code') || 'EMPLOYEE';
      const emp_name = localStorage.getItem('user_name') || 'Employee';

      const metadataRows = [
        { 'Expense Reimbursement Report': 'Employee Code', 'Value': emp_code },
        { 'Expense Reimbursement Report': 'Employee Name', 'Value': emp_name },
        { 'Expense Reimbursement Report': 'Report Type', 'Value': 'Expense History' },
        { 'Expense Reimbursement Report': 'Period', 'Value': activeRange },
        { 'Expense Reimbursement Report': 'Date Range', 'Value': dateRange.from && dateRange.to ? `${dateRange.from} to ${dateRange.to}` : 'All Time' },
        { 'Expense Reimbursement Report': 'Total Records', 'Value': myRequests.length },
        { 'Expense Reimbursement Report': 'Generated On', 'Value': new Date().toLocaleString() },
        { 'Expense Reimbursement Report': '', 'Value': '' }
      ];

      const dataRows = myRequests.map(req => {
        let expenseData = {};
        try {
          expenseData = JSON.parse(req.employee_reason);
        } catch (e) {}
        
        return {
          'Date': new Date(req.created_at).toLocaleDateString(),
          'Category': expenseData.category || 'N/A',
          'Amount (₹)': expenseData.amount || 0,
          'Description': expenseData.description || 'N/A',
          'Status': req.status || 'Pending'
        };
      });

      const allRows = [...metadataRows, ...dataRows];
      const ws = XLSX.utils.json_to_sheet(allRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Expense History');
      XLSX.writeFile(wb, `Expense_History_${activeRange}_${new Date().getTime()}.xlsx`);
    } catch (err) {
      console.error('Download error:', err);
      alert('Download failed');
    }
  };

  useEffect(() => {
    fetchMyRequests();
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current.click();
  };

  const barData = {
    labels: ['Ret', 'Sub', 'Blk', 'Prog', 'Appr', 'Comm'],
    datasets: [{
      label: 'Volume',
      data: chartValues,
      borderRadius: 8,
      backgroundColor: '#6366F1',
      hoverBackgroundColor: '#4338CA',
    }],
  };

  const doughnutData = {
    labels: ['Travel', 'Food', 'Office', 'Misc'],
    datasets: [{
      data: [45, 25, 20, 10],
      backgroundColor: ['#6366F1', '#10B981', '#F59E0B', '#F43F5E'],
      borderWidth: 0,
      cutout: '82%',
      borderRadius: 10,
      spacing: 5
    }],
  };

  return (
    <div className="min-h-screen bg-[#FDFEFF] text-slate-900 font-inter antialiased">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@700;800&display=swap');
        .font-inter { font-family: 'Inter', sans-serif; }
        .font-display { font-family: 'Plus Jakarta Sans', sans-serif; }
      `}</style>

      <div className="fixed top-0 right-0 -z-10 w-[500px] h-[500px] bg-indigo-50/50 rounded-full blur-[120px]" />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
          <div>
            <h1 className="font-display text-3xl font-[800] tracking-tight text-slate-900">Expense <span className="text-indigo-600">Analytics</span></h1>
            <p className="text-sm font-medium text-slate-400">Manage organizational liquidity ({activeRange})</p>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex bg-slate-100 p-1 rounded-xl mr-2">
              {['30D', '60D', '90D'].map(range => (
                <button 
                  key={range}
                  onClick={() => setActiveRange(range)}
                  className={`px-3 py-1 text-[11px] font-bold rounded-lg transition-all ${activeRange === range ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  {range}
                </button>
              ))}
            </div>
            <motion.button
              whileHover={{ scale: 1.03, backgroundColor: '#4338CA' }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowForm(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold shadow-lg transition-colors"
            >
              <PlusIcon className="h-4 w-4 stroke-[3px]" /> New Request
            </motion.button>
          </div>
        </header>

        {/* STATUS CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          {Object.entries({
            'Submitted': { amount: realExpenseData['Submitted']?.amount || 0, pending: realExpenseData['Submitted']?.pending || 0, color: '#6366F1', icon: DocumentTextIcon, bg: 'bg-indigo-500' },
            'In Progress': { amount: realExpenseData['In Progress']?.amount || 0, pending: realExpenseData['In Progress']?.pending || 0, color: '#3B82F6', icon: ClockIcon, bg: 'bg-blue-500' },
            'Approved': { amount: realExpenseData['Approved']?.amount || 0, pending: realExpenseData['Approved']?.pending || 0, color: '#10B981', icon: CheckCircleIcon, bg: 'bg-emerald-500' },
            'Rejected': { amount: realExpenseData['Rejected']?.amount || 0, pending: realExpenseData['Rejected']?.pending || 0, color: '#EF4444', icon: XCircleIcon, bg: 'bg-rose-500' }
          }).map(([status, config]) => (
            <motion.div
              key={status}
              whileHover={{ y: -4 }}
              className="bg-white border border-slate-100 p-3.5 rounded-2xl shadow-sm hover:shadow-md transition-all group"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-lg ${config.bg} bg-opacity-10 transition-all`}>
                  <config.icon className="h-4 w-4" style={{ color: config.color }} />
                </div>
                <EllipsisHorizontalIcon className="h-4 w-4 text-slate-300" />
              </div>
              <p className="text-lg font-extrabold text-slate-800 tracking-tight">₹{config.amount.toLocaleString()}</p>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{status}</p>
              <div className="mt-3 flex items-center gap-1.5">
                <span className="text-[10px] font-bold text-indigo-500">{config.pending} active</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ANALYTICS GRID */}
        <div className="grid lg:grid-cols-12 gap-6 items-start mb-8">
          <div className="lg:col-span-4 bg-white border border-slate-100 p-6 rounded-[2rem] shadow-sm relative">
            <h3 className="text-sm font-bold text-slate-800 mb-6">Spending Composition</h3>
            <div className="h-64 relative">
              <Doughnut data={doughnutData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-2xl font-display font-black text-slate-900">84%</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 bg-white border border-slate-100 p-6 rounded-[2rem] shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-8">Operational Velocity ({activeRange})</h3>
            <div className="h-[300px]">
              <Bar data={barData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
            </div>
          </div>
        </div>

        {/* EXPENSE HISTORY TABLE */}
        <div className="bg-white border border-slate-100 rounded-[2rem] shadow-sm overflow-hidden mb-8">
          <div className="p-6 border-b border-slate-100 bg-slate-50/50">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800 font-display">Expense History</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowHistoryModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition"
                >
                  <ClockIcon className="w-3.5 h-3.5" />
                  View History
                </button>
                <button
                  onClick={handleDownloadLedger}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold transition"
                >
                  <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                  Download
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-50">
                {myRequests.length > 0 ? (
                  myRequests.map((req, idx) => {
                    let expenseData = {};
                    try {
                      expenseData = JSON.parse(req.employee_reason);
                    } catch (e) {}
                    
                    return (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm text-slate-600">{new Date(req.created_at).toLocaleDateString()}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-slate-900">{expenseData.category || 'N/A'}</td>
                        <td className="px-6 py-4 text-sm font-bold text-indigo-600">₹{(expenseData.amount || 0).toLocaleString()}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{expenseData.description || 'N/A'}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                            req.status === 'HR_APPROVED' ? 'bg-emerald-100 text-emerald-700' :
                            req.status === 'MANAGER_REJECTED' ? 'bg-red-100 text-red-700' :
                            req.status === 'PENDING_HR' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {req.status === 'HR_APPROVED' ? 'Approved' :
                             req.status === 'MANAGER_REJECTED' ? 'Rejected' :
                             req.status === 'PENDING_HR' ? 'In Progress' : 'Submitted'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-slate-400">
                      <DocumentTextIcon className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                      <p>No expense records found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <footer className="mt-8 flex justify-center gap-3">
             <motion.button 
               onClick={() => setShowHistoryModal(true)}
               whileHover={{ y: -2, backgroundColor: '#2563eb' }}
               whileTap={{ scale: 0.98 }}
               className="px-6 py-2 bg-blue-600 text-white rounded-full text-xs font-bold flex items-center gap-2 shadow-xl transition-all"
             >
               <ClockIcon className="h-3 w-3" /> View History
             </motion.button>
             <motion.button 
               onClick={handleDownloadLedger}
               whileHover={{ y: -2, backgroundColor: '#1e293b' }}
               whileTap={{ scale: 0.98 }}
               className="px-6 py-2 bg-slate-900 text-white rounded-full text-xs font-bold flex items-center gap-2 shadow-xl transition-all"
             >
               <ArrowDownTrayIcon className="h-3 w-3" /> Download {activeRange} Report
             </motion.button>
        </footer>
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md overflow-hidden border border-white/20"
            >
              <div className="bg-indigo-600 p-8 text-white relative">
                <h2 className="text-2xl font-display font-extrabold">New Request</h2>
                <p className="text-indigo-100 text-xs mt-1">Submit your business expense for audit</p>
                <button onClick={() => setShowForm(false)} className="absolute top-8 right-8 text-indigo-200 hover:text-white transition-colors cursor-pointer">
                    <XCircleIcon className="h-7 w-7" />
                </button>
              </div>
              
              <div className="p-8 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Type</label>
                    <div className="relative group">
                      <select 
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold appearance-none outline-none focus:ring-2 focus:ring-indigo-500/20"
                      >
                        {EXPENSE_CATEGORIES.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                      <ChevronDownIcon className="absolute right-3 top-3 h-4 w-4 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Value</label>
                    <input 
                      type="number" 
                      placeholder="₹0.00" 
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500/20" 
                    />
                  </div>
                </div>

                {/* CONDITIONAL OTHER FIELD */}
                <AnimatePresence>
                  {selectedCategory === "Other" && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }} 
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="space-y-1 overflow-hidden"
                    >
                      <label className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest ml-1">Manual Expense Type</label>
                      <input 
                        type="text" 
                        placeholder="e.g. Subscription, Repairs..." 
                        value={customCategory}
                        onChange={(e) => setCustomCategory(e.target.value)}
                        className="w-full bg-indigo-50/50 border border-indigo-200 rounded-xl px-4 py-2.5 text-sm font-bold outline-none" 
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* DESCRIPTION FIELD */}
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Description</label>
                  <textarea 
                    rows="3" 
                    placeholder="Enter purpose of this expense..." 
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500/20"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Attachment</label>
                  <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png" />
                  <div onClick={triggerFileUpload} className="w-full border-2 border-dashed border-slate-200 rounded-2xl py-6 flex flex-col items-center justify-center bg-slate-50/50 hover:bg-indigo-50/50 cursor-pointer transition-all">
                    {selectedFile ? (
                      <div className="flex flex-col items-center text-center px-4">
                        <DocumentIcon className="h-6 w-6 text-indigo-600 mb-1" />
                        <span className="text-[10px] font-bold text-indigo-600 truncate max-w-xs">{selectedFile.name}</span>
                      </div>
                    ) : (
                      <>
                        <PlusIcon className="h-5 w-5 text-indigo-400 mb-1" />
                        <span className="text-[10px] font-bold text-slate-400">Upload Receipt</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button onClick={() => setShowForm(false)} className="flex-1 py-3 text-xs font-bold text-slate-400 hover:text-rose-500 transition-colors cursor-pointer">Discard</button>
                  <motion.button
                    whileHover={{ scale: 1.02, backgroundColor: '#059669' }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSubmitExpense}
                    className="flex-[2] py-3 bg-emerald-500 text-white rounded-xl text-xs font-bold shadow-lg transition-all uppercase tracking-widest cursor-pointer"
                  >
                    Submit Request
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {showHistoryModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }}
              className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md overflow-hidden border border-white/20"
            >
              <div className="bg-blue-600 p-8 text-white relative">
                <h2 className="text-2xl font-display font-extrabold">View Expense History</h2>
                <p className="text-blue-100 text-xs mt-1">Select date range to filter expense records</p>
                <button onClick={() => {
                  setShowHistoryModal(false);
                  setDateRange({ from: '', to: '' });
                }} className="absolute top-8 right-8 text-blue-200 hover:text-white transition-colors cursor-pointer">
                    <XCircleIcon className="h-7 w-7" />
                </button>
              </div>
              
              <div className="p-8 space-y-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">From Date</label>
                  <input
                    type="date"
                    value={dateRange.from}
                    onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">To Date</label>
                  <input
                    type="date"
                    value={dateRange.to}
                    onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button onClick={() => setDateRange({ from: '', to: '' })} className="flex-1 py-3 text-xs font-bold text-slate-400 hover:text-rose-500 transition-colors cursor-pointer">Clear Filter</button>
                  <motion.button
                    whileHover={{ scale: 1.02, backgroundColor: '#1d4ed8' }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowHistoryModal(false)}
                    className="flex-[2] py-3 bg-blue-600 text-white rounded-xl text-xs font-bold shadow-lg transition-all uppercase tracking-widest cursor-pointer"
                  >
                    Apply Filter
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}